/**
 * Kalender-spezifischer Oberflaechenzustand.
 *
 * Getrennt vom allgemeinen UI-Store, weil sich der Drag-Zustand waehrend einer
 * Bewegung bis zu 60-mal pro Sekunde aendert. Komponenten, die ihn nicht
 * brauchen, sollen davon nicht neu rendern.
 */
import { create } from 'zustand';

import type { CalendarOccurrence, LocalDate, LocalDateTime, Task } from '@/domain/types';
import type { Span } from '@/domain/calendar/geometry';

/** Was gerade gezogen wird. `null` heisst: keine Bewegung im Gange. */
export type DragState =
  | null
  | {
      kind: 'create';
      date: LocalDate;
      span: Span;
    }
  | {
      kind: 'move';
      occurrence: CalendarOccurrence;
      date: LocalDate;
      span: Span;
    }
  | {
      kind: 'resize';
      occurrence: CalendarOccurrence;
      edge: 'start' | 'end';
      date: LocalDate;
      span: Span;
    }
  | {
      /** Eine Aufgabe wird aus einer Liste ins Raster gezogen (F-16). */
      kind: 'task';
      taskId: string;
      title: string;
      areaId: string | null;
      /** Dauer des entstehenden Zeitblocks. */
      minutes: number;
      /** `null`, solange der Zeiger nicht ueber dem Raster steht. */
      date: LocalDate | null;
      span: Span;
    };

interface CalendarState {
  /** Der Tag, um den herum die aktuelle Ansicht liegt. */
  anchor: LocalDate;
  drag: DragState;
  /** Vorkommen, das gerade in der Seitenleiste offen ist (F-04 c). */
  selected: CalendarOccurrence | null;
  /** Entwurf fuer einen neu aufgezogenen Termin. */
  creating: { start: LocalDateTime; end: LocalDateTime; allDay: boolean } | null;

  setAnchor: (date: LocalDate) => void;
  setDrag: (drag: DragState) => void;
  /** Beginnt das Ziehen einer Aufgabe; das Raster uebernimmt Position und Ablage. */
  startTaskDrag: (task: Pick<Task, 'id' | 'title' | 'areaId' | 'estimateMinutes'>) => void;
  select: (occurrence: CalendarOccurrence | null) => void;
  startCreating: (draft: { start: LocalDateTime; end: LocalDateTime; allDay: boolean } | null) => void;
}

export const useCalendarStore = create<CalendarState>()((set) => ({
  anchor: toDate(new Date()),
  drag: null,
  selected: null,
  creating: null,

  setAnchor: (anchor) => set({ anchor }),
  setDrag: (drag) => set({ drag }),
  startTaskDrag: (task) => {
    const minutes = Math.min(Math.max(task.estimateMinutes ?? 60, 15), 8 * 60);
    set({
      drag: {
        kind: 'task', taskId: task.id, title: task.title, areaId: task.areaId,
        minutes, date: null, span: { startMinutes: 0, endMinutes: minutes },
      },
    });
  },
  select: (selected) => set({ selected }),
  startCreating: (creating) => set({ creating }),
}));

function toDate(d: Date): LocalDate {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
